import { createApi } from "@reduxjs/toolkit/query/react";
import { ITEM, TagId, TagType } from "../../configs";
import {
  HttpMethod,
  Item,
  ItemDetails,
  IWriteItem,
  PaginatedResponse,
  SearchByDisplayName,
} from "../../types";
import { createBaseQuery } from "../helpers/baseQueryHelper";

const itemApi = createApi({
  reducerPath: ITEM.reducerPath,
  baseQuery: createBaseQuery(ITEM.baseUrl),
  tagTypes: ITEM.tagTypes,
  endpoints(builder) {
    return {
      fetchItems: builder.query<
        PaginatedResponse<Item>,
        { restaurantId: string; searchQuery: SearchByDisplayName }
      >({
        query: ({ restaurantId, searchQuery }) => {
          return {
            url: `/search/${restaurantId}/restaurant`,
            method: HttpMethod.GET,
            params: searchQuery,
          };
        },
        providesTags: () => [{ type: TagType.Item, id: TagId.LIST }],
      }),
      createItem: builder.mutation<ItemDetails, IWriteItem>({
        query: (body) => ({
          url: "",
          method: HttpMethod.POST,
          body,
        }),
        invalidatesTags: () => [{ type: TagType.Item, id: TagId.LIST }],
      }),
      updateItem: builder.mutation<void, { itemId: string; body: IWriteItem }>({
        query: ({ itemId, body }) => ({
          url: `/${itemId}`,
          method: HttpMethod.PUT,
          body,
        }),
        invalidatesTags: (_result, _error, { itemId }) => [
          { type: TagType.Item, id: TagId.LIST },
          { type: TagType.Item, id: itemId },
        ],
      }),
      fetchItemDetails: builder.query<ItemDetails, string>({
        query: (itemId) => {
          return {
            url: `/${itemId}`,
            method: HttpMethod.GET,
          };
        },
        providesTags: (_result, _error, itemId) => [
          { type: TagType.Item, id: itemId },
        ],
      }),
      deleteItem: builder.mutation<void, string>({
        query: (itemId) => ({
          url: `/${itemId}`,
          method: HttpMethod.DELETE,
        }),
        invalidatesTags: (_result, _error, itemId) => [
          { type: TagType.Item, id: TagId.LIST },
          { type: TagType.Item, id: itemId },
        ],
      }),
      toggleItemStock: builder.mutation<
        void,
        { itemId: string; haveStock: boolean }
      >({
        query: ({ itemId, haveStock }) => ({
          url: `/${itemId}/stock`,
          method: HttpMethod.PATCH,
          params: { haveStock },
        }),
        invalidatesTags: () => [{ type: TagType.Item, id: TagId.LIST }],
      }),
      updateItemImage: builder.mutation<void, { itemId: string; file: File }>({
        query: ({ itemId, file }) => {
          const formData = new FormData();
          formData.append("image", file);

          return {
            url: `/${itemId}/image`,
            method: HttpMethod.PATCH,
            body: formData,
          };
        },
        invalidatesTags: (_result, _error, { itemId }) => [
          { type: TagType.Restaurant, id: itemId },
        ],
      }),
    };
  },
});

export const {
  useCreateItemMutation,
  useDeleteItemMutation,
  useFetchItemDetailsQuery,
  useFetchItemsQuery,
  useLazyFetchItemDetailsQuery,
  useUpdateItemMutation,
  useToggleItemStockMutation,
  useUpdateItemImageMutation,
} = itemApi;

export { itemApi };
